import { useState } from 'react'
import BeamCalculator from './BeamCalculator'
import DevLengthCalculator from './DevLengthCalculator'
import ConcreteBeamDesign from './ConcreteBeamDesign'
import References from './References'

function Dashboard({ user, onLogout }) {
  const [activeCategory, setActiveCategory] = useState('analysis')
  const [activeCalculator, setActiveCalculator] = useState(null)

  const calculators = {
    references: [
      { title: 'Reinforcing Bar Properties', description: 'Bar sizes, areas, weights, perimeters, and area per foot at common spacings.', features: ['Bar #3 through #18', 'Metric designations', 'Area per foot table'], available: true, calculatorId: 'references' },
      { title: 'Concrete Clear Cover', description: 'Minimum cover requirements by exposure condition and member type per ACI 318-19.', features: ['Table 20.5.1.3.1', 'Quick reference summary', 'Interior / Exterior / Earth'], available: true, calculatorId: 'references' },
      { title: 'Beam Diagrams & Formulas', description: 'Shear, moment, and deflection formulas for common beam loading conditions.', features: ['Simple spans', 'Cantilevers', 'Fixed-end beams', 'Deflection limits'], available: true, calculatorId: 'references' },
    ],
    analysis: [
      { title: 'Beam Analysis', description: 'Shear and bending moment analysis for simply supported beams with point and distributed loads.', features: ['Reaction calculations', 'Shear force diagram', 'Bending moment diagram'], available: true, calculatorId: 'beam' }
    ],
    concrete: [
      { title: 'Beam Design', description: 'Complete rectangular concrete beam design per ACI 318-19.', features: ['Flexural design', 'Shear design', 'Crack control', 'Deflection checks'], available: true, calculatorId: 'concretebeam' },
      { title: 'Development Length', description: 'Calculate development and splice lengths for reinforcing bars per ACI 318-19.', features: ['Straight bar development', 'Hooked bar development', 'Compression development', 'Tension & compression splices'], available: true, calculatorId: 'devlength' },
      { title: 'Column Design', description: 'Design concrete columns for axial load and combined bending.', features: ['Interaction diagrams', 'Biaxial bending', 'Slenderness checks'], available: false }
    ],
    steel: [
      { title: 'Beam Design', description: 'AISC 360 steel beam design and analysis.', features: ['Flexural capacity', 'Shear capacity', 'Deflection checks'], available: false }
    ]
  }

  const categories = [
    { id: 'analysis', label: 'Analysis Tools', count: calculators.analysis.length },
    { id: 'concrete', label: 'Concrete Design', count: calculators.concrete.length },
    { id: 'steel', label: 'Steel Design', count: calculators.steel.length },
    { id: 'references', label: 'References', count: calculators.references.length },
  ]

  if (activeCalculator === 'beam') {
    return <BeamCalculator onBack={() => setActiveCalculator(null)} />
  }
  if (activeCalculator === 'devlength') {
    return <DevLengthCalculator onBack={() => setActiveCalculator(null)} />
  }
  if (activeCalculator === 'concretebeam') {
    return <ConcreteBeamDesign onBack={() => setActiveCalculator(null)} />
  }
  if (activeCalculator === 'references') {
    return <References onBack={() => setActiveCalculator(null)} />
  }

  const LogoSVG = () => (
    <svg viewBox="0 0 28 28" width="28" height="28">
      <defs><linearGradient id="dlg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#1e3a5f"/><stop offset="100%" stopColor="#0f2440"/></linearGradient></defs>
      <rect x="0" y="0" width="28" height="28" rx="5" fill="url(#dlg)"/>
      <polygon points="7,21 7,7 9.5,7 14,13 18.5,7 21,7 21,21 18.5,21 18.5,12 14,18 9.5,12 9.5,21" fill="white"/>
    </svg>
  )

  const AvatarSVG = () => (
    <svg viewBox="0 0 26 26" width="26" height="26">
      <defs><linearGradient id="dav" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#1e3a5f"/><stop offset="100%" stopColor="#0f2440"/></linearGradient></defs>
      <circle cx="13" cy="13" r="13" fill="url(#dav)"/>
      <text x="13" y="17" textAnchor="middle" fill="white" fontSize="11" fontWeight="700" fontFamily="-apple-system,sans-serif">{user.email.charAt(0).toUpperCase()}</text>
    </svg>
  )

  const showMoreNote = activeCategory === 'analysis' || activeCategory === 'steel' || activeCategory === 'concrete'

  return (
    <div style={st.page}>
      <style>{`*{margin:0;padding:0;box-sizing:border-box}`}</style>

      <header style={st.topBar}>
        <div style={st.topBarInner}>
          <div style={st.logoGroup}><LogoSVG /><span style={st.logoText}>MRVN</span></div>
          <div style={st.topBarRight}>
            <div style={st.userPill}><AvatarSVG /><span style={st.userEmail}>{user.email}</span></div>
            <button onClick={onLogout} style={st.logoutBtn}>Log Out</button>
          </div>
        </div>
      </header>

      <div style={st.welcomeBar}>
        <div style={st.welcomeInner}>
          <h1 style={st.welcomeTitle}>Engineering Design Suite</h1>
          <p style={st.welcomeSub}>Professional structural analysis and design tools.</p>
        </div>
      </div>

      <main style={st.main}>
        <div style={st.tabRow}>
          {categories.map(cat => (
            <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
              style={{ ...st.tab, ...(activeCategory === cat.id ? st.tabActive : {}) }}>
              {cat.label}
              <span style={{ ...st.tabCount, ...(activeCategory === cat.id ? st.tabCountActive : {}) }}>{cat.count}</span>
            </button>
          ))}
        </div>

        {activeCategory === 'references' ? (
          <div>
            <div style={st.refIntro}>
              Downloadable reference tables and formula sheets. These open as PDFs you can save, print, or use on-site.
            </div>
            <div style={st.grid}>
              {calculators.references.map((calc, index) => (
                <div key={index} style={{ ...st.card, ...st.cardAvail }}>
                  <div style={st.cardHeader}></div>
                  <h3 style={st.cardTitle}>{calc.title}</h3>
                  <p style={st.cardDesc}>{calc.description}</p>
                  <div style={st.featureList}>
                    {calc.features.map((feature, i) => (
                      <div key={i} style={st.featureItem}>
                        <span style={st.checkMark}>&#10003;</span>
                        <span style={st.featureText}>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 'auto' }}>
                    <button onClick={() => setActiveCalculator('references')} style={st.openBtn}>Browse References <span>&rarr;</span></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={st.grid}>
            {calculators[activeCategory].map((calc, index) => (
              <div key={index} style={{ ...st.card, ...(calc.available ? st.cardAvail : {}) }}>
                <div style={st.cardHeader}>
                  {!calc.available && <span style={st.statusComing}>Coming Soon</span>}
                </div>
                <h3 style={st.cardTitle}>{calc.title}</h3>
                <p style={st.cardDesc}>{calc.description}</p>
                <div style={st.featureList}>
                  {calc.features.map((feature, i) => (
                    <div key={i} style={st.featureItem}>
                      <span style={st.checkMark}>&#10003;</span>
                      <span style={st.featureText}>{feature}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 'auto' }}>
                  {calc.available ? (
                    <button onClick={() => setActiveCalculator(calc.calculatorId)} style={st.openBtn}>Start Analysis <span>&rarr;</span></button>
                  ) : (
                    <button style={st.disabledBtn} disabled>Coming Soon</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {showMoreNote && (
          <div style={st.moreNote}>More {activeCategory === 'analysis' ? 'analysis' : activeCategory === 'concrete' ? 'concrete design' : 'steel design'} tools in development.</div>
        )}
      </main>

      <footer style={st.footer}>
        <p>&copy; 2026 MRVN &mdash; Structural Engineering Platform</p>
        <span style={st.version}>v0.1</span>
      </footer>
    </div>
  )
}

const st = {
  page: { minHeight: '100vh', background: '#0a0f1e', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', color: '#e2e8f0', display: 'flex', flexDirection: 'column' },
  topBar: { background: '#080c18', borderBottom: '1px solid rgba(30,58,95,0.2)', padding: '0 2rem', position: 'sticky', top: 0, zIndex: 50 },
  topBarInner: { maxWidth: '1300px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0' },
  logoGroup: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  logoText: { fontWeight: 800, fontSize: '1.15rem', letterSpacing: '0.08em', color: '#f1f5f9' },
  topBarRight: { display: 'flex', alignItems: 'center', gap: '1rem' },
  userPill: { display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '100px', padding: '0.3rem 0.75rem 0.3rem 0.3rem' },
  userEmail: { fontSize: '0.8rem', color: '#94a3b8', fontWeight: 500 },
  logoutBtn: { padding: '0.4rem 0.85rem', background: 'transparent', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '5px', color: '#ef4444', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  welcomeBar: { background: 'linear-gradient(180deg,rgba(30,58,95,0.08) 0%,transparent 100%)', borderBottom: '1px solid rgba(255,255,255,0.04)', padding: '0 2rem' },
  welcomeInner: { maxWidth: '1300px', margin: '0 auto', padding: '2rem 0' },
  welcomeTitle: { fontSize: '1.5rem', fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.02em', marginBottom: '0.25rem' },
  welcomeSub: { fontSize: '0.9rem', color: '#64748b' },
  main: { maxWidth: '1300px', margin: '0 auto', padding: '2rem', flex: 1, width: '100%' },
  tabRow: { display: 'flex', gap: '0.25rem', marginBottom: '2rem', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  tab: { padding: '0.65rem 1.25rem', background: 'none', border: 'none', borderBottom: '2px solid transparent', color: '#64748b', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '-1px', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tabActive: { color: '#3b82f6', borderBottomColor: '#3b82f6' },
  tabCount: { fontSize: '0.7rem', fontWeight: 700, background: 'rgba(100,116,139,0.15)', color: '#64748b', padding: '0.1rem 0.45rem', borderRadius: '100px' },
  tabCountActive: { background: 'rgba(30,58,95,0.3)', color: '#3b82f6' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: '1.25rem' },
  card: { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '1.5rem', display: 'flex', flexDirection: 'column', transition: 'all 0.25s ease' },
  cardAvail: { borderColor: 'rgba(30,58,95,0.35)', background: 'linear-gradient(135deg,rgba(30,58,95,0.08) 0%,rgba(15,36,64,0.03) 100%)' },
  cardHeader: { marginBottom: '1rem', minHeight: '24px' },
  statusComing: { display: 'inline-flex', alignItems: 'center', fontSize: '0.72rem', fontWeight: 600, color: '#475569', background: 'rgba(100,116,139,0.08)', border: '1px solid rgba(100,116,139,0.1)', padding: '0.2rem 0.6rem', borderRadius: '100px' },
  cardTitle: { fontSize: '1.15rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.4rem' },
  cardDesc: { fontSize: '0.88rem', color: '#64748b', lineHeight: 1.5, marginBottom: '1rem' },
  featureList: { display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1.25rem', flex: 1 },
  featureItem: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  checkMark: { color: '#3b82f6', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 },
  featureText: { fontSize: '0.85rem', color: '#94a3b8' },
  openBtn: { width: '100%', padding: '0.7rem 1.25rem', background: '#3b82f6', border: 'none', borderRadius: '6px', color: 'white', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontFamily: 'inherit', boxShadow: '0 0 20px rgba(59,130,246,0.12)' },
  disabledBtn: { width: '100%', padding: '0.7rem 1.25rem', background: 'rgba(100,116,139,0.08)', border: '1px solid rgba(100,116,139,0.1)', borderRadius: '6px', color: '#475569', fontWeight: 600, fontSize: '0.9rem', cursor: 'not-allowed', fontFamily: 'inherit' },
  refIntro: { padding: '1rem 1.25rem', background: 'rgba(30,58,95,0.1)', border: '1px solid rgba(30,58,95,0.25)', borderRadius: '8px', fontSize: '0.88rem', color: '#94a3b8', borderLeft: '3px solid #3b82f6', marginBottom: '1.5rem' },
  moreNote: { marginTop: '1.5rem', padding: '1rem 1.25rem', border: '1px dashed rgba(255,255,255,0.06)', borderRadius: '8px', textAlign: 'center', fontSize: '0.85rem', color: '#475569' },
  footer: { borderTop: '1px solid rgba(255,255,255,0.04)', padding: '1.5rem 2rem', textAlign: 'center', fontSize: '0.8rem', color: '#334155', marginTop: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem' },
  version: { fontSize: '0.7rem', color: '#1e293b', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.04)', padding: '0.15rem 0.5rem', borderRadius: '100px', fontWeight: 600 },
}

export default Dashboard
